import { CreateTransactionRequest } from 'src/app/test-transactions-client';
import { Account } from './account';

type SourceLabelAccount = Pick<Account, 'requestSourceLabel'>;

// Source values that are only ever an account's label for "Request" (e.g. Visa's "Reactive"), never stored as is.
// They're not offered on their own, so they don't show up for other clients or before an account has its label set.
const labelOnlySources: string[] = [CreateTransactionRequest.SourceEnum.Reactive];

export function isLabelOnlySource(source: string | undefined): boolean {
  return !!source && labelOnlySources.includes(source);
}

// How "Request" is shown for a client (e.g. "Reactive" for Visa)
export function getRequestSourceOption(account: SourceLabelAccount | undefined): string {
  return account?.requestSourceLabel || CreateTransactionRequest.SourceEnum.Request;
}

// Source dropdown options for a client: its label (e.g. "Reactive" for Visa) replaces "Request"
export function getSourceOptions(account: SourceLabelAccount | undefined): string[] {
  return Object.values(CreateTransactionRequest.SourceEnum)
    .filter((source) => !isLabelOnlySource(source))
    .map((source) => (source === CreateTransactionRequest.SourceEnum.Request ? getRequestSourceOption(account) : source));
}

export interface RecordSourceState {
  // Source dropdown options for the record
  options: string[];
  // The value the form shows, and sends back on save
  selected: string | undefined;
}

// Source options and selected value for an existing record. `account` is the record's client from the accounts list
// (undefined while it loads); `recordSource` is the source as the API returned it. The selected value is always one the
// API accepts for the record, even when the accounts list disagrees with the API (e.g. a label set or cleared meanwhile).
export function getRecordSourceState(account: SourceLabelAccount | undefined, recordSource: string | undefined): RecordSourceState {
  // Until accounts load, a record shown with a label (e.g. "Reactive") already tells us its client's label
  const clientLabels = account ?? (isLabelOnlySource(recordSource) ? { requestSourceLabel: recordSource } : undefined);
  const options = getSourceOptions(clientLabels);

  if (!recordSource || options.includes(recordSource)) {
    return { options, selected: recordSource };
  }

  // A label the account no longer has becomes the account's own option ("Request" or its current label)
  if (isLabelOnlySource(recordSource)) {
    return { options, selected: getRequestSourceOption(account) };
  }

  // A stored source missing from the options (e.g. "Request" for a client that just got a label) stays selectable,
  // since the API always accepts stored sources
  return { options: [...options, recordSource], selected: recordSource };
}
